
import React, { useState, useEffect } from 'react';
import CountUp from './CountUp';

interface HeroProps {
  onOpenModal: () => void;
}

const sliderImages = [
  "https://i.imgur.com/hHqITQA.png",
  "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?q=60&w=800&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?q=60&w=800&auto=format&fit=crop"
];

const Hero: React.FC<HeroProps> = ({ onOpenModal }) => {
  const [scrollY, setScrollY] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const [bgLoaded, setBgLoaded] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % sliderImages.length);
    }, 4000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(interval);
    };
  }, []);

  return (
    <section 
      className="relative pt-20 md:pt-24 pb-4 bg-[#F2F4F4] flex flex-col"
      role="region"
      aria-label="Hero Section"
    >
      <div className="max-w-[1440px] mx-auto w-full px-4 md:px-8 lg:px-12">
        
        {/* Adjusted min-height to use svh (Small Viewport Height) to account for browser chrome */}
        <div className="relative w-full rounded-[0.4rem] md:rounded-[0.8rem] overflow-hidden shadow-xl flex flex-col min-h-[70svh] lg:min-h-[80svh] max-h-[900px] bg-gray-200">
          
          <div className="absolute inset-0 z-0 overflow-hidden">
            <img 
              src="https://i.imgur.com/7WMSVKv.png" 
              className={`w-full h-full object-cover brightness-[0.75] scale-110 transition-opacity duration-700 ${bgLoaded ? 'opacity-100' : 'opacity-0'}`} 
              alt="Wind turbines landscape backdrop" 
              loading="eager"
              // @ts-ignore
              fetchpriority="high"
              onLoad={() => setBgLoaded(true)}
              style={{
                transform: `translateY(${scrollY * 0.15}px)`,
                transition: 'transform 0.1s linear, opacity 0.7s ease-out'
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent"></div>
          </div>

          <div className="relative z-10 p-6 md:p-14 lg:p-20 flex-1 flex flex-col justify-center">
            <div className="max-w-4xl space-y-4 md:space-y-8">
              <p className="text-white font-extrabold text-[9px] md:text-xs uppercase tracking-[0.25em] reveal-item" style={{ transitionDelay: '0.1s' }}>
                #1 Energy provider in the world
              </p>
              
              <h1 className="fluid-heading-hero font-black leading-[0.95] tracking-tight text-white drop-shadow-lg reveal-item" style={{ transitionDelay: '0.2s' }}>
                New Energy <br />
                <span className="text-white/75">for the Future</span>
              </h1>

              <div className="flex flex-wrap items-center gap-4 md:gap-8 pt-4 reveal-item" style={{ transitionDelay: '0.3s' }}>
                <button 
                  onClick={onOpenModal}
                  className="flex items-center gap-2 md:gap-3 text-white text-xs md:text-base font-bold border-b-2 border-white pb-1 hover:text-emerald-400 hover:border-emerald-400 transition-all group focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  aria-label="Contact us to get in touch"
                >
                  Get in touch
                  <svg className="w-3 h-3 md:w-4 md:h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                    <path d="M7 17l10-10M7 7h10v10" />
                  </svg>
                </button>
                <button 
                  className="flex items-center gap-2 md:gap-3 text-white text-xs md:text-base font-bold border-b-2 border-white pb-1 hover:text-emerald-400 hover:border-emerald-400 transition-all group focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  aria-label="View our services"
                >
                  Our services
                  <svg className="w-3 h-3 md:w-4 md:h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                    <path d="M7 17l10-10M7 7h10v10" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Slide indicator card - Now visible on mobile with adjusted scale and placement */}
          <div className="absolute top-0 right-3 md:right-12 lg:right-24 bottom-[110px] sm:bottom-[150px] md:bottom-[180px] lg:bottom-[220px] z-20 flex flex-col justify-center pointer-events-none reveal-item" style={{ transitionDelay: '0.5s' }}>
            <div 
              className="bg-white/5 backdrop-blur-md border border-white/10 p-3 md:p-5 rounded-lg w-40 sm:w-56 lg:w-64 shadow-2xl transition-all duration-500 pointer-events-auto hover:scale-105"
              style={{
                transform: `translateY(${16 + scrollY * -0.1}px)`,
                transition: 'transform 0.1s linear'
              }}
            >
              <div className="relative rounded-md overflow-hidden mb-3 md:mb-4 aspect-square bg-white/10 img-shimmer">
                <div 
                  className="flex transition-transform duration-700 ease-in-out h-full w-full"
                  style={{ transform: `translateX(-${activeSlide * 100}%)` }}
                >
                  {sliderImages.map((img, idx) => (
                    <img 
                      key={idx}
                      src={img} 
                      className="w-full h-full object-cover flex-shrink-0" 
                      alt={`Project Slide ${idx + 1}`} 
                      loading={idx === 0 ? "eager" : "lazy"}
                      // @ts-ignore
                      fetchpriority={idx === 0 ? "high" : "low"}
                    />
                  ))}
                </div>
                
                <div className="absolute top-2 right-2 md:top-3 md:right-3 flex gap-1 z-10">
                  {sliderImages.map((_, idx) => (
                    <button 
                      key={idx}
                      onClick={() => setActiveSlide(idx)}
                      className={`w-1 h-1 md:w-1.5 md:h-1.5 rounded-full transition-all duration-300 pointer-events-auto outline-none ${activeSlide === idx ? 'bg-white scale-125' : 'bg-white/50'}`}
                    ></button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between px-0.5">
                 <div className="space-y-0.5">
                   <p className="text-white/80 text-[7px] md:text-[10px] font-bold uppercase tracking-widest">Discover Our</p>
                   <p className="text-white font-bold text-[10px] md:text-sm">Recent Project</p>
                 </div>
                 <button 
                  className="w-7 h-7 md:w-10 md:h-10 rounded-full border border-white/30 flex items-center justify-center cursor-pointer hover:bg-white/20 transition-colors pointer-events-auto"
                 >
                    <svg className="w-3 h-3 md:w-4 md:h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                      <path d="M7 17l10-10" />
                    </svg>
                 </button>
              </div>
            </div>
          </div>

          {/* Bottom stats bar - Preserving desktop placement on mobile with partial width anchoring */}
          <div className="absolute bottom-[-0.5px] right-[-0.5px] z-30 bg-[#F2F4F4] pt-3 pl-3 md:pt-6 md:pl-10 lg:pt-8 lg:pl-14 rounded-tl-[0.6rem] md:rounded-tl-[1.1rem] w-[94%] xs:w-[88%] md:w-3/5 lg:w-1/2 border-none ring-1 ring-[#F2F4F4] reveal-item" style={{ transitionDelay: '0.4s' }}>
            <div className="absolute -top-[0.59rem] md:-top-[1.09rem] right-0 w-[0.6rem] md:w-[1.1rem] h-[0.6rem] md:h-[1.1rem] text-[#F2F4F4] pointer-events-none">
              <svg width="100%" height="100%" viewBox="0 0 100 100" fill="none" preserveAspectRatio="none">
                <path d="M100 100V0C100 55.2285 55.2285 100 0 100H100Z" fill="currentColor"/>
              </svg>
            </div>
            <div className="absolute bottom-0 -left-[0.59rem] md:-left-[1.09rem] w-[0.6rem] md:w-[1.1rem] h-[0.6rem] md:h-[1.1rem] text-[#F2F4F4] pointer-events-none">
              <svg width="100%" height="100%" viewBox="0 0 100 100" fill="none" preserveAspectRatio="none">
                <path d="M100 100V0C100 55.2285 55.2285 100 0 100H100Z" fill="currentColor"/>
              </svg>
            </div>
            <div className="grid grid-cols-3 gap-1 md:gap-4 lg:gap-8 items-start pb-3 md:pb-6 lg:pb-8">
              <div className="space-y-0.5 md:space-y-1">
                <CountUp end={6} suffix=" mil" />
                <p className="text-[#525B63] text-[7px] md:text-[10px] lg:text-[11px] font-bold uppercase tracking-widest leading-tight">
                  The company's<br className="hidden md:block" /> annual net income
                </p>
              </div>
              <div className="space-y-0.5 md:space-y-1">
                <CountUp end={315} />
                <p className="text-[#525B63] text-[7px] md:text-[10px] lg:text-[11px] font-bold uppercase tracking-widest leading-tight">
                  Projects completed<br className="hidden md:block" /> worldwide
                </p>
              </div>
              <div className="space-y-0.5 md:space-y-1">
                <CountUp end={120} suffix="K" />
                <p className="text-[#525B63] text-[7px] md:text-[10px] lg:text-[11px] font-bold uppercase tracking-widest leading-tight">
                  Employees work in<br className="hidden md:block" /> all parts of the world
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;
